// SubmitButton.js
import React, { useEffect, useState } from "react";
import { FIBER_PRODUCT_CODE, ARCARS_VENUE_ID, ARCARS_SIGNUP_SOURCE } from "../assets/constant";
import { createCustomerService } from "../services/apiServices";
import { generateGUID, deleteCookieByName } from "../config/utils";
import {
  Modal,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";

import CircularProgress from "@mui/material/CircularProgress";
import FiberLogo from "../assets/images/Boingo-Fiber-Logo-Horizontal.svg";


const parseMessageWithLink = (message, url, anchorText) => {
    const parts = message.split(/(\[link\].*?\[\/link\])/g);

    return parts.map((part, index) => {
        if (part.startsWith('[link]') && part.endsWith('[/link]')) {
            // This part is the placeholder. Create the React anchor element.
            const textInPlaceholder = part.replace('[link]', '').replace('[/link]', '');
            const linkTextToUse = anchorText || textInPlaceholder;
            
            return <a key={index} href={url}>{linkTextToUse}</a>;
        } else {
            return <span key={index}>{part}</span>;
        }
    });
};


function SubmitButtonForSignup({
  finalCustomerInfo,
  braintreeInstanceObject,
  deviceDataKount,
  paymentType,
  setPaymentType,
  milstarCCGlobal,
  setMilstarCCGlobal,
  paymentAndLoginValidationErrors,
}) {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setIsLoading(false);
    setErrorMessage(null);
  };
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [useHrefErrorMessage, setUseHrefErrorMessage] = useState(false);
  const [hrefErrorMessage, setHrefErrorMessage] = useState(
    {

    }
  );

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  useEffect(() => {
    if (paymentType === "applePay" || paymentType === "PayPal") {
      setIsSubmitDisabled(false);
    }
    if (paymentType === "hostedField" || paymentType === "milstar") {
      setIsSubmitDisabled(true);
      // Define all the keys that should be in paymentAndLoginValidationErrors, (values are same for hostedfields and milstar)
      const requiredErrorKeys = [
        "number",
        "cvv",
        "expirationDate",
        "postalCode",
        "username",
        "password",
      ];

      // Check if all keys are set in paymentValidationErrors
      const allKeysSet = requiredErrorKeys.every(
        (key) => key in paymentAndLoginValidationErrors
      );

      if (allKeysSet) {
        // Check if all error messages are empty
        const allErrorsResolved = Object.values(
          paymentAndLoginValidationErrors
        ).every((error) => error === "");
        setIsSubmitDisabled(!allErrorsResolved);
      }
    }
  }, [paymentAndLoginValidationErrors, paymentType]);

  const handleSignupAndPayment = async (event) => {
    event.preventDefault();

    handleOpen();
    setIsLoading(true);

    let paymentTypeObj = {};

    // If User selects Milstar payment
    if (paymentType === "milstar") {
      paymentTypeObj.payment_cc = milstarCCGlobal;
      createCustomerWithPayment(paymentTypeObj);
    } else if (paymentType === "applePay" || paymentType === "PayPal") {
      paymentTypeObj.payment_braintree = {
        ...braintreeInstanceObject,
        device_data: deviceDataKount,
      };
      createCustomerWithPayment(paymentTypeObj);
    }
    // If User selects Braintree Hosted Fields (default)
    else if (paymentType === "hostedField") {
      braintreeInstanceObject.tokenize(async function (tokenizeErr, payload) {
        if (tokenizeErr) {
          console.error("Error hosted fields tokenizing:", tokenizeErr);
          setErrorMessage(tokenizeErr.message);
          setIsLoading(false);
          return;
        }

        paymentTypeObj.payment_braintree = {
          nonce: payload.nonce,
          payment_type: "Credit Card",
          device_data: deviceDataKount,
        };
        createCustomerWithPayment(paymentTypeObj);
      });
      return false;
    }
  };

  const createCustomerWithPayment = async (paymentObj) => {
    //prepare customer data object

    let customerDataObj = {
      ...finalCustomerInfo,
      ...paymentObj,
      product_codes: [FIBER_PRODUCT_CODE],
      transaction_id: generateGUID(),
      scc: ARCARS_SIGNUP_SOURCE,
      venue_id: ARCARS_VENUE_ID,
      GSID: generateGUID().toUpperCase(),
    };

    createCustomerService(customerDataObj)
      .then((response) => {
        console.log("Successful customer creation: ", response.data);
        deleteCookieByName("customerData");
        window.location.hash = "/fiber-welcome";
      })
      .catch((error) => {
        console.log("Failed to create customer: ", error);
        setIsLoading(false);
        if (error && error.response && error.response.status === 502){
          setUseHrefErrorMessage(true);
          setErrorMessage(`Your account creation is taking longer than expected. Please disconnect your box, wait 20 minutes and reconnect. If you have a hard time connecting or just need to talk to a care agent, please call customer service at [link]support[/link]`);
        }
        else {
          setUseHrefErrorMessage(false);

          error.message && error.response.data
          ? setErrorMessage(error.response.data.message)
          : setErrorMessage(error.message);
        }
      });
  };

  return (
    <>
      {/* {JSON.stringify(paymentType)} */}
      {/* {JSON.stringify(paymentAndLoginValidationErrors)} */}
      {/* {JSON.stringify(finalCustomerInfo)} */}

      <Button
        disabled={isSubmitDisabled || isLoading}
        sx={{ mb: "100px", width: "100%" }}
        disableElevation
        variant="contained"
        type="submit"
        onClick={handleSignupAndPayment}
        data-test-id="signup-btn"
      >
        Complete signup
      </Button>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          <img src={FiberLogo} alt="Fiber Logo" />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: "center" }}>
            {isLoading && <CircularProgress sx={{ m: 3 }} />}
            <Typography sx={{ mt: 2 }}>
              Please wait while we create your account
            </Typography>
            {useHrefErrorMessage && (
              <>
              <Typography sx={{ mt: 2 }} color="primary" data-test-id="signup-error">
                  {parseMessageWithLink(errorMessage,"https://support.boingo.com/FamilyHousingFiber/s/contactsupport","support")}
                </Typography>

                <Button sx={{ margin: "15px auto 0" }} onClick={handleClose} data-test-id="dialog-close-btn">
                  Close
                </Button>
              </>
            )}
            {errorMessage && !useHrefErrorMessage && (
              <>
                <Typography sx={{ mt: 2 }} color="primary" data-test-id="signup-error">
                  {errorMessage}
                </Typography>

                <Button sx={{ margin: "15px auto 0" }} onClick={handleClose} data-test-id="dialog-close-btn">
                  Close
                </Button>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SubmitButtonForSignup;
